import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled, { css } from 'styled-components';
import classNames from 'classnames';

// constants
import { colors, darken } from '../../style';
import { actionColor } from '../../style/theme-helpers';

export interface DocTabProps {
  className?: string;
  href?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  active?: boolean;
  size?: keyof typeof sizes;
  componentClass?: React.ElementType | string;
  to?: string | RouteComponentProps['location'];
  onClick?: React.MouseEventHandler<any>;
  id?: string;
}

export const sizes = {
  lg: css`
    font-size: 1rem;
  `,
};

const TabComponent = ({
  className,
  disabled,
  active,
  componentClass: Component = 'button',
  to,
  ...rest
}: DocTabProps) => (
  <Component
    type={Component === 'button' ? 'button' : undefined}
    activeClassName={to ? 'active' : undefined}
    to={to}
    className={classNames(className, { disabled, active })}
    disabled={disabled}
    role="tab"
    aria-selected={!!active}
    {...rest}
  />
);

const DocTab = styled(TabComponent)`
  border: 1px solid ${colors.gray300};
  border-top-right-radius: 4px;
  border-top-left-radius: 4px;
  border-top: 3px solid ${colors.gray600};
  border-bottom: none;
  background: white;
  color: ${colors.gray600};
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 8px 16px;
  transition: 0.2s ease-in-out;
  flex: 1 1 0;
  width: 0;

  &.active {
    background: ${colors.surfaceSecondary};
    color: ${actionColor};
    border-top: 3px solid ${colors.highlight};
  }

  ${props => sizes[props.size || 'lg']};

  &:not([disabled]),
  &:not(.disabled) {
    cursor: pointer;
  }

  &:hover:not(.disabled) {
    background: ${colors.surfaceSecondary};
    &:active {
      background: ${darken(0.01, colors.surfaceSecondary)};
    }
  }

  &:hover.active:not(.disabled) {
    color: ${actionColor};

    &:active {
      background: ${darken(0.02, colors.surfaceSecondary)};
      border-right-color: ${darken(0.02, colors.surfaceSecondary)};
      border-left-color: ${darken(0.02, colors.surfaceSecondary)};
    }
  }

  &:active {
    background: ${colors.gray300};
  }

  &.disabled {
    color: ${colors.gray600};
    background: ${colors.gray100};
    cursor: not-allowed;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px ${colors.sky};
  }
`;

export default DocTab;
